import {ErrorMessage, Field, Form, Formik, FormikHelpers} from 'formik'
import {FC, useEffect, useState} from 'react'
import * as Yup from 'yup'
import {Form as BsForm, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import {useDispatch, useSelector} from 'react-redux'
import {fetchAsyncUserDetails, getAllUserDetails} from '../Overview/OverviewSlice/UserDetailsSlice'
import {useUpdateBounceNotificationSettingMutation} from '../../../../../services/cellcast'
import {toast} from 'react-toastify'

type NotificationSettings = {
  bounceThresholdEnable: boolean
  bounceThreshold: number
  bounceNotificationEmail: boolean
  bounceNotificationSms: boolean
}

const validationSchema = Yup.object({
  bounceThresholdEnable: Yup.boolean(),
  bounceThreshold: Yup.number().when('bounceThresholdEnable', {
    is: true,
    then: (schema) => schema.required('Bounce threshold is required').typeError('Must be a number'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

const BounceNotification: FC = (props) => {
  const dispatch = useDispatch()
  const userValues = useSelector(getAllUserDetails)

  const [initialValue, setInitialValue] = useState<NotificationSettings>({
    bounceThresholdEnable: false,
    bounceThreshold: 0,
    bounceNotificationEmail: false,
    bounceNotificationSms: false,
  })

  // get RTK action
  const [update, {isLoading}] = useUpdateBounceNotificationSettingMutation()

  const onSubmit = (
    values: NotificationSettings,
    formikHelpers: FormikHelpers<NotificationSettings>
  ) => {
    update(values)
      .then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            toast.error(res?.data?.message)
          } else {
            dispatch(fetchAsyncUserDetails())
            toast.success(res?.data?.message)
          }
        } else if ('error' in res) {
          const resError = res.error as any
          if (resError.data?.message) {
            toast.error(resError.data?.message || '')
          } else {
            toast.error(res?.error.toString() || '')
          }
        }
      })
      .finally(() => {
        formikHelpers.setSubmitting(false)
      })
  }

  useEffect(() => {
    if (userValues) {
      if (userValues?.bounceNotificationSettings) {
        setInitialValue(userValues?.bounceNotificationSettings)
      }
    }
  }, [userValues])

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header'>
        <div className='card-title'>
          <h3 className='fs-2'>Bounce Notification</h3>
        </div>
      </div>
      <div className='card-body'>
        <Formik
          enableReinitialize
          initialValues={initialValue}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({isSubmitting, values, errors, touched, isValid, setFieldValue}) => (
            <Form>
              <div className='first-row'>
                <div className='row align-items-center'>
                  <div className='col-xl-4 col-lg-5 col-md-5 col-sm-12 col-12'>
                    <label className='title-h5'>Enable Notification</label>
                    <OverlayTrigger
                      placement='top'
                      overlay={<Tooltip>Notifications will be sent every 3 hours.</Tooltip>}
                    >
                      <i className='fas fa-exclamation-circle ms-2 fs-7'></i>
                    </OverlayTrigger>
                  </div>
                  <div className='col-xl-8 col-lg-7 col-md-7 col-sm-12 col-12'>
                    <BsForm.Check
                      onChange={(e: any) => {
                        setFieldValue('bounceThresholdEnable', !values.bounceThresholdEnable)
                      }}
                      checked={values.bounceThresholdEnable}
                      type='switch'
                      id='enable-threshold'
                    />
                  </div>
                </div>
                <div className='row align-items-center'>
                  <div className='col-xl-4 col-lg-5 col-md-5 col-sm-12 col-12'>
                    <h5 className='title-h5'>Threshold</h5>
                  </div>
                  <div className='col-xl-2 col-lg-2 col-md-2 col-sm-2 col-2 mt-5'>
                    <div className='input-group'>
                      <Field
                        type='text'
                        className='form-control form-control-lg form-control-solid'
                        name='bounceThreshold'
                        placeholder=''
                      />
                      <span
                        className='input-group-text'
                        style={{border: 'none', boxShadow: 'none'}}
                      >
                        %
                      </span>
                    </div>
                    <ErrorMessage className='text-danger' name='bounceThreshold' component='span' />
                  </div>
                </div>
                <div className='row align-items-center'>
                  <div className='col-xl-4 col-lg-5 col-md-5 col-sm-12 col-12'>
                    <h5 className='title-h5'>Notification</h5>
                  </div>
                  <div className='col-xl-2 col-lg-2 col-md-2 col-sm-2 col-2 mt-5'>
                    <div className='d-flex align-items-center mt-3'>
                      <label className='form-check form-check-inline form-check-solid me-5'>
                        <input
                          data-testid='cbCommunicationEmail'
                          className='form-check-input'
                          type='checkbox'
                          defaultChecked={values?.bounceNotificationEmail === true}
                          checked={values?.bounceNotificationEmail === true}
                          onChange={(e: any) => {
                            setFieldValue(
                              'bounceNotificationEmail',
                              !values?.bounceNotificationEmail
                            )
                          }}
                        />
                        <span className='fw-bold ps-2 fs-6'>Email</span>
                      </label>

                      <label className='form-check form-check-inline form-check-solid'>
                        <input
                          data-testid='cbCommunicationPhone'
                          className='form-check-input'
                          type='checkbox'
                          defaultChecked={values?.bounceNotificationSms === true}
                          checked={values?.bounceNotificationSms === true}
                          value={values?.bounceNotificationSms ? 'on' : 'off'}
                          onChange={(e: any) =>
                            setFieldValue('bounceNotificationSms', !values.bounceNotificationSms)
                          }
                        />
                        <span className='fw-bold ps-2 fs-6'>Phone</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className='row border-top mt-2 p-4'>
                <div className='col-md-12 text-center text-xl-end text-lg-end text-md-end'>
                  <button
                    data-testid='btnBounceNotification'
                    className='btn btn-primary fw-500'
                    type='submit'
                    disabled={isLoading}
                  >
                    {isLoading ? <Spinner /> : 'Submit'}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default BounceNotification
